/**
 * Таблица координат в стиле гугл-листа (как AccountsTable, но сетка простая:
 * колонка = страна, ячейка = пара «lat, lng»):
 *  - клик = выделение, драг / shift-клик = диапазон, Cmd/Ctrl/Alt+клик —
 *    несмежное выделение, Ctrl+A = все ячейки; стрелки двигают фокус;
 *  - Ctrl+C / Ctrl+X / Ctrl+V / Delete по выделению (TSV, совместимо с гугл-
 *    таблицей): вставка заполняет ячейки от левого-верхнего угла выделения,
 *    в пустые позиции колонки координаты ДОБАВЛЯЮТСЯ;
 *  - двойной клик / Enter / начать печатать — правка ячейки, Esc — отмена;
 *  - Cmd+Z / Ctrl+Z — откат последних правок (до 50 шагов);
 *  - двойной клик по заголовку — переименование страны (колонки);
 *  - автоскролл при драге у краёв.
 */
import { useEffect, useMemo, useRef, useState, type ClipboardEvent } from 'react';
import { api } from '../api/client.js';

export interface Coord {
  id: string;
  lat: string;
  lng: string;
  note: string | null;
  usedAt: string | null;
}

export interface CountryBlock {
  country: string;
  items: Coord[];
}

interface Props {
  blocks: CountryBlock[];
  onReload: () => void;
  onError: (msg: string) => void;
  /** Прячем использованные (переключатель на странице). */
  hideUsed: boolean;
  onAddColumn: () => void;
}

interface Pos { r: number; c: number }

const keyOf = (r: number, c: number) => `${r}:${c}`;
const parseKey = (k: string): Pos => {
  const [r, c] = k.split(':');
  return { r: Number(r), c: Number(c) };
};

function rectKeys(a: Pos, b: Pos): string[] {
  const out: string[] = [];
  for (let r = Math.min(a.r, b.r); r <= Math.max(a.r, b.r); r++) {
    for (let c = Math.min(a.c, b.c); c <= Math.max(a.c, b.c); c++) out.push(keyOf(r, c));
  }
  return out;
}

/** «51.1, 16.8» / «51.1 16.8» / «51.1;16.8» → пара строк. */
function parsePair(text: string): { lat: string; lng: string } | null {
  const m = text.trim().match(/^(-?\d+(?:[.,]\d+)?)\s*[,;\s]\s*(-?\d+(?:[.,]\d+)?)$/);
  if (!m) return null;
  return { lat: m[1]!.replace(',', '.'), lng: m[2]!.replace(',', '.') };
}

/** Шаг undo: что вернуть, если нажали Cmd+Z. */
type UndoStep =
  | { kind: 'edit'; id: string; lat: string; lng: string }
  | { kind: 'create'; ids: string[] }
  | { kind: 'delete'; rows: { country: string; lat: string; lng: string }[] };

export function CoordinatesTable({ blocks, onReload, onError, hideUsed, onAddColumn }: Props) {
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [anchor, setAnchor] = useState<Pos | null>(null);
  const [focus, setFocus] = useState<Pos | null>(null);
  const [editing, setEditing] = useState<{ r: number; c: number; value: string } | null>(null);
  const [renaming, setRenaming] = useState<{ index: number; value: string } | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const wrapRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{ active: boolean; start: Pos; base: Set<string> } | null>(null);
  const undoRef = useRef<UndoStep[][]>([]);

  const columns = useMemo(
    () => blocks.map((b) => (hideUsed ? b.items.filter((i) => !i.usedAt) : b.items)),
    [blocks, hideUsed],
  );
  // Одна пустая строка снизу — чтобы дописывать координаты прямо в таблице.
  const rowCount = columns.reduce((m, c) => Math.max(m, c.length), 0) + 1;

  const cellAt = (r: number, c: number): Coord | undefined => columns[c]?.[r];

  // ---------- выделение ----------
  function cellMouseDown(e: React.MouseEvent, r: number, c: number) {
    if (e.button !== 0) return;
    setEditing(null);
    wrapRef.current?.focus({ preventScroll: true });
    const k = keyOf(r, c);
    if (e.shiftKey && anchor) {
      const rect = rectKeys(anchor, { r, c });
      setSel(e.metaKey || e.ctrlKey ? new Set([...sel, ...rect]) : new Set(rect));
      setFocus({ r, c });
      return;
    }
    if (e.metaKey || e.ctrlKey || e.altKey) {
      const next = new Set(sel);
      if (next.has(k)) next.delete(k); else next.add(k);
      setSel(next);
      setAnchor({ r, c });
      setFocus({ r, c });
      dragRef.current = { active: true, start: { r, c }, base: next };
      return;
    }
    setSel(new Set([k]));
    setAnchor({ r, c });
    setFocus({ r, c });
    dragRef.current = { active: true, start: { r, c }, base: new Set() };
  }

  function cellMouseEnter(r: number, c: number) {
    const d = dragRef.current;
    if (!d?.active) return;
    setSel(new Set([...d.base, ...rectKeys(d.start, { r, c })]));
    setFocus({ r, c });
  }

  useEffect(() => {
    const up = () => { if (dragRef.current) dragRef.current.active = false; };
    const outside = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setSel(new Set());
        setFocus(null);
      }
    };
    document.addEventListener('mouseup', up);
    document.addEventListener('mousedown', outside);
    return () => {
      document.removeEventListener('mouseup', up);
      document.removeEventListener('mousedown', outside);
    };
  }, []);

  // автоскролл у краёв при драге
  useEffect(() => {
    const mouse = { x: 0, y: 0 };
    let raf = 0;
    const EDGE = 44;
    const step = () => {
      raf = 0;
      const scroller = wrapRef.current?.querySelector<HTMLElement>('.coord-scroll');
      if (!dragRef.current?.active || !scroller) return;
      const rect = scroller.getBoundingClientRect();
      let vy = 0;
      let vx = 0;
      if (mouse.y > rect.bottom - EDGE) vy = Math.min(26, (mouse.y - (rect.bottom - EDGE)) * 0.6);
      else if (mouse.y < rect.top + EDGE) vy = -Math.min(26, ((rect.top + EDGE) - mouse.y) * 0.6);
      if (mouse.x > rect.right - EDGE) vx = Math.min(22, (mouse.x - (rect.right - EDGE)) * 0.6);
      else if (mouse.x < rect.left + EDGE) vx = -Math.min(22, ((rect.left + EDGE) - mouse.x) * 0.6);
      if (vy || vx) {
        scroller.scrollTop += vy;
        scroller.scrollLeft += vx;
        const el = document.elementFromPoint(
          Math.min(Math.max(mouse.x, rect.left + 2), rect.right - 2),
          Math.min(Math.max(mouse.y, rect.top + 2), rect.bottom - 2),
        );
        const td = el?.closest?.('[data-cr]') as HTMLElement | null;
        if (td) cellMouseEnter(Number(td.dataset.cr), Number(td.dataset.cc));
      }
      raf = requestAnimationFrame(step);
    };
    const move = (e: MouseEvent) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      if (dragRef.current?.active && !raf) raf = requestAnimationFrame(step);
    };
    document.addEventListener('mousemove', move);
    return () => {
      document.removeEventListener('mousemove', move);
      if (raf) cancelAnimationFrame(raf);
    };
  });

  // ---------- правки ----------
  async function saveCell(r: number, c: number, raw: string) {
    const country = blocks[c]?.country;
    if (!country) return;
    const existing = cellAt(r, c);
    const text = raw.trim();
    setBusy(true);
    try {
      if (text === '') {
        if (existing) await deleteIds([existing.id], [{ country, lat: existing.lat, lng: existing.lng }]);
        return;
      }
      const pair = parsePair(text);
      if (!pair) {
        onError(`«${text}» — не похоже на координаты (нужно «51.16438, 16.80438»)`);
        return;
      }
      if (existing) {
        undoRef.current.push([{ kind: 'edit', id: existing.id, lat: existing.lat, lng: existing.lng }]);
        await api(`/api/coordinates/${existing.id}`, {
          method: 'PATCH',
          body: JSON.stringify({ lat: pair.lat, lng: pair.lng }),
        });
      } else {
        const res = await api<{ created: Coord[] }>('/api/coordinates', {
          method: 'POST',
          body: JSON.stringify({ country, bulk: `${pair.lat} ${pair.lng}` }),
        });
        undoRef.current.push([{ kind: 'create', ids: res.created.map((x) => x.id) }]);
      }
      onReload();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не получилось сохранить');
    } finally {
      setBusy(false);
      undoRef.current = undoRef.current.slice(-50);
    }
  }

  async function deleteIds(ids: string[], rows: { country: string; lat: string; lng: string }[]) {
    if (ids.length === 0) return;
    undoRef.current.push([{ kind: 'delete', rows }]);
    undoRef.current = undoRef.current.slice(-50);
    await api('/api/coordinates/bulk-delete', { method: 'POST', body: JSON.stringify({ ids }) });
    onReload();
  }

  async function deleteSelection() {
    const ids: string[] = [];
    const rows: { country: string; lat: string; lng: string }[] = [];
    for (const k of sel) {
      const { r, c } = parseKey(k);
      const item = cellAt(r, c);
      const country = blocks[c]?.country;
      if (item && country) { ids.push(item.id); rows.push({ country, lat: item.lat, lng: item.lng }); }
    }
    if (ids.length === 0) return;
    setBusy(true);
    try {
      await deleteIds(ids, rows);
      setSel(new Set());
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не получилось удалить');
    } finally {
      setBusy(false);
    }
  }

  async function undo() {
    const step = undoRef.current.pop();
    if (!step) return;
    setBusy(true);
    try {
      for (const s of step) {
        if (s.kind === 'edit') {
          await api(`/api/coordinates/${s.id}`, { method: 'PATCH', body: JSON.stringify({ lat: s.lat, lng: s.lng }) });
        } else if (s.kind === 'create') {
          await api('/api/coordinates/bulk-delete', { method: 'POST', body: JSON.stringify({ ids: s.ids }) });
        } else {
          const byCountry = new Map<string, string[]>();
          for (const row of s.rows) {
            if (!byCountry.has(row.country)) byCountry.set(row.country, []);
            byCountry.get(row.country)!.push(`${row.lat} ${row.lng}`);
          }
          for (const [country, lines] of byCountry) {
            await api('/api/coordinates', { method: 'POST', body: JSON.stringify({ country, bulk: lines.join('\n') }) });
          }
        }
      }
      onReload();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Откат не удался');
    } finally {
      setBusy(false);
    }
  }

  // ---------- клавиатура и буфер ----------
  function selectionTsv(): string | null {
    if (sel.size === 0) return null;
    const cells = [...sel].map(parseKey);
    const r1 = Math.min(...cells.map((p) => p.r));
    const r2 = Math.max(...cells.map((p) => p.r));
    const c1 = Math.min(...cells.map((p) => p.c));
    const c2 = Math.max(...cells.map((p) => p.c));
    const lines: string[] = [];
    for (let r = r1; r <= r2; r++) {
      const row: string[] = [];
      for (let c = c1; c <= c2; c++) {
        const item = sel.has(keyOf(r, c)) ? cellAt(r, c) : undefined;
        row.push(item ? `${item.lat}, ${item.lng}` : '');
      }
      lines.push(row.join('\t'));
    }
    return lines.join('\n');
  }

  async function onPaste(e: ClipboardEvent) {
    if (editing || sel.size === 0) return;
    const text = e.clipboardData?.getData('text/plain');
    if (!text) return;
    e.preventDefault();
    const cells = [...sel].map(parseKey);
    const r0 = Math.min(...cells.map((p) => p.r));
    const c0 = Math.min(...cells.map((p) => p.c));
    const rows = text.replace(/\r/g, '').split('\n').filter((l) => l.trim() !== '');
    // Один блок значений — раскладываем по колонкам/строкам от угла выделения.
    const grid = rows.map((line) => (line.includes('\t') ? line.split('\t') : [line]));
    const byCountry = new Map<string, string[]>();
    const edits: { id: string; lat: string; lng: string; prev: Coord }[] = [];
    for (let i = 0; i < grid.length; i++) {
      for (let j = 0; j < grid[i]!.length; j++) {
        const raw = grid[i]![j]!.trim();
        if (!raw) continue;
        const pair = parsePair(raw);
        if (!pair) continue;
        const r = r0 + i;
        const c = c0 + j;
        const country = blocks[c]?.country;
        if (!country) continue;
        const existing = cellAt(r, c);
        if (existing) edits.push({ id: existing.id, lat: pair.lat, lng: pair.lng, prev: existing });
        else {
          if (!byCountry.has(country)) byCountry.set(country, []);
          byCountry.get(country)!.push(`${pair.lat} ${pair.lng}`);
        }
      }
    }
    if (edits.length === 0 && byCountry.size === 0) return;
    setBusy(true);
    try {
      const step: UndoStep[] = edits.map((x) => ({ kind: 'edit' as const, id: x.id, lat: x.prev.lat, lng: x.prev.lng }));
      for (const x of edits) {
        await api(`/api/coordinates/${x.id}`, { method: 'PATCH', body: JSON.stringify({ lat: x.lat, lng: x.lng }) });
      }
      for (const [country, lines] of byCountry) {
        const res = await api<{ created: Coord[] }>('/api/coordinates', {
          method: 'POST',
          body: JSON.stringify({ country, bulk: lines.join('\n') }),
        });
        step.push({ kind: 'create', ids: res.created.map((x) => x.id) });
      }
      undoRef.current.push(step);
      undoRef.current = undoRef.current.slice(-50);
      onReload();
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Вставка не удалась');
    } finally {
      setBusy(false);
    }
  }

  type ClipLike = { preventDefault(): void; clipboardData: DataTransfer | null };

  function onCopy(e: ClipLike) {
    if (editing || renaming) return;
    const tsv = selectionTsv();
    if (tsv == null || !e.clipboardData) return;
    e.preventDefault();
    e.clipboardData.setData('text/plain', tsv);
  }

  function onCut(e: ClipLike) {
    if (editing || renaming) return;
    const tsv = selectionTsv();
    if (tsv == null || !e.clipboardData) return;
    e.preventDefault();
    e.clipboardData.setData('text/plain', tsv);
    deleteSelection();
  }

  // Фолбэк на уровне документа: на Windows Chrome фокус после драга не всегда
  // остаётся внутри контейнера, и события copy/cut/paste/keydown до него не
  // доходят (16.09: «копируется только первая ячейка» — только на винде).
  // Срабатывает, когда в таблице есть выделение и курсор ввода не в поле.
  const latest = useRef({ onCopy, onCut, onPaste, onKeyDown, sel });
  latest.current = { onCopy, onCut, onPaste, onKeyDown, sel };
  useEffect(() => {
    const typing = () => {
      const el = document.activeElement as HTMLElement | null;
      return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable);
    };
    const active = (e: Event) => !e.defaultPrevented && latest.current.sel.size > 0 && !typing();
    const copy = (e: globalThis.ClipboardEvent) => { if (active(e)) latest.current.onCopy(e); };
    const cut = (e: globalThis.ClipboardEvent) => { if (active(e)) latest.current.onCut(e); };
    const paste = (e: globalThis.ClipboardEvent) => {
      if (!active(e)) return;
      latest.current.onPaste(e as unknown as ClipboardEvent);
    };
    const key = (e: globalThis.KeyboardEvent) => {
      if (!active(e)) return;
      if (wrapRef.current?.contains(e.target as Node)) return; // дойдёт до onKeyDown контейнера
      const mod = e.metaKey || e.ctrlKey;
      if (mod && ['c', 'x', 'v'].includes(e.key.toLowerCase())) {
        // Без фокуса и DOM-выделения браузер copy/cut/paste не порождает вовсе.
        // Возвращаем фокус в контейнер синхронно — родное событие придёт в
        // onCopy/onCut/onPaste. Никаких navigator.clipboard и запросов прав.
        wrapRef.current?.focus({ preventScroll: true });
        return;
      }
      latest.current.onKeyDown(e as unknown as React.KeyboardEvent);
    };
    document.addEventListener('copy', copy);
    document.addEventListener('cut', cut);
    document.addEventListener('paste', paste);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('copy', copy);
      document.removeEventListener('cut', cut);
      document.removeEventListener('paste', paste);
      document.removeEventListener('keydown', key);
    };
  }, []);

  function onKeyDown(e: React.KeyboardEvent) {
    if (editing || renaming) return;
    const mod = e.metaKey || e.ctrlKey;
    if (mod && e.key.toLowerCase() === 'a') {
      e.preventDefault();
      const all: string[] = [];
      for (let r = 0; r < rowCount; r++) for (let c = 0; c < blocks.length; c++) all.push(keyOf(r, c));
      setSel(new Set(all));
      return;
    }
    if ((mod || e.altKey) && e.key.toLowerCase() === 'z') {
      e.preventDefault();
      undo();
      return;
    }
    // Ctrl+C / Ctrl+X обрабатываются нативными событиями copy/cut (см.
    // onCopy/onCut): запись через navigator.clipboard из keydown перебивалась
    // родным copy-событием с DOM-выделением первой ячейки — в гугл-таблицу
    // вставлялась только она (16.09).
    if (e.key === 'Delete' || e.key === 'Backspace') {
      if (sel.size > 0) {
        e.preventDefault();
        deleteSelection();
      }
      return;
    }
    if (!focus) return;
    const move = (dr: number, dc: number) => {
      e.preventDefault();
      const r = Math.max(0, Math.min(rowCount - 1, focus.r + dr));
      const c = Math.max(0, Math.min(blocks.length - 1, focus.c + dc));
      if (e.shiftKey && anchor) setSel(new Set(rectKeys(anchor, { r, c })));
      else { setSel(new Set([keyOf(r, c)])); setAnchor({ r, c }); }
      setFocus({ r, c });
    };
    if (e.key === 'ArrowUp') return move(-1, 0);
    if (e.key === 'ArrowDown') return move(1, 0);
    if (e.key === 'ArrowLeft') return move(0, -1);
    if (e.key === 'ArrowRight') return move(0, 1);
    if (e.key === 'Enter') {
      e.preventDefault();
      const item = cellAt(focus.r, focus.c);
      setEditing({ r: focus.r, c: focus.c, value: item ? `${item.lat}, ${item.lng}` : '' });
      return;
    }
    if (e.key.length === 1 && !mod) {
      setEditing({ r: focus.r, c: focus.c, value: e.key });
    }
  }

  async function renameCountry(index: number, to: string) {
    const from = blocks[index]?.country;
    setRenaming(null);
    if (!from || !to.trim() || from === to.trim()) return;
    setBusy(true);
    try {
      await api('/api/coordinates/rename-country', {
        method: 'POST',
        body: JSON.stringify({ from, to: to.trim() }),
      });
      onReload();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Переименование не удалось');
    } finally {
      setBusy(false);
    }
  }

  /** Координаты выделения (только непустые ячейки). */
  function selectedCoords(): Coord[] {
    const out: Coord[] = [];
    for (const k of sel) {
      const { r, c } = parseKey(k);
      const item = cellAt(r, c);
      if (item) out.push(item);
    }
    return out;
  }

  /** Галочка в ячейке: если ячейка входит в выделение — помечаем ВСЮ группу
   *  (выделил отработанные → один клик → все ушли), иначе только её. */
  async function toggleUsed(c: Coord, fromCell = true) {
    const group = fromCell ? selectedCoords() : [];
    const batch = group.length > 1 && group.some((x) => x.id === c.id) ? group : [c];
    const used = !c.usedAt;
    setBusy(true);
    try {
      if (batch.length === 1) {
        await api(`/api/coordinates/${batch[0]!.id}`, { method: 'PATCH', body: JSON.stringify({ used }) });
      } else {
        await api('/api/coordinates/bulk-used', {
          method: 'POST',
          body: JSON.stringify({ ids: batch.map((x) => x.id), used }),
        });
      }
      if (batch.length > 1) setSel(new Set());
      onReload();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  }

  /** Кнопка в нижней панели: пометить всё выделение использованным. */
  async function markSelectionUsed() {
    const group = selectedCoords();
    if (group.length === 0) return;
    setBusy(true);
    try {
      await api('/api/coordinates/bulk-used', {
        method: 'POST',
        body: JSON.stringify({ ids: group.map((x) => x.id), used: true }),
      });
      setSel(new Set());
      onReload();
    } catch (e) {
      onError(e instanceof Error ? e.message : 'Не получилось');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="card coord-card"
      ref={wrapRef}
      tabIndex={0}
      onKeyDown={onKeyDown}
      onCopy={onCopy}
      onCut={onCut}
      onPaste={onPaste}
      style={{ padding: 0, overflow: 'hidden', outline: 'none', opacity: busy ? 0.75 : 1 }}
    >
      <div className="coord-scroll" style={{ overflow: 'auto', maxHeight: 'calc(100vh - 230px)' }}>
        <table className="coord-table">
          <thead>
            <tr>
              <th style={{ width: 46 }}>#</th>
              {blocks.map((b, ci) => (
                <th key={b.country} style={{ minWidth: 176 }} onDoubleClick={() => setRenaming({ index: ci, value: b.country })}>
                  {renaming?.index === ci ? (
                    <input
                      autoFocus
                      className="coord-head-input"
                      value={renaming.value}
                      onChange={(e) => setRenaming({ index: ci, value: e.target.value })}
                      onBlur={() => renameCountry(ci, renaming.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') renameCountry(ci, renaming.value);
                        if (e.key === 'Escape') setRenaming(null);
                      }}
                    />
                  ) : (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} title="Двойной клик — переименовать">
                      <span>{b.country}</span>
                      <span style={{ color: 'var(--text-subtle)', fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>
                        {columns[ci]?.length ?? 0}
                      </span>
                    </div>
                  )}
                </th>
              ))}
              <th style={{ width: 44 }}>
                <button className="row-open-btn" title="Добавить страну (колонку)" style={{ color: 'var(--accent)' }} onClick={onAddColumn}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14" /></svg>
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {Array.from({ length: rowCount }, (_, r) => (
              <tr key={r}>
                <td className="coord-idx">{r + 1}</td>
                {blocks.map((_, c) => {
                  const item = cellAt(r, c);
                  const isSel = sel.has(keyOf(r, c));
                  const isFocus = focus?.r === r && focus?.c === c;
                  const isEditing = editing?.r === r && editing?.c === c;
                  return (
                    <td
                      key={c}
                      data-cr={r}
                      data-cc={c}
                      className={`coord-td${isSel ? ' sel' : ''}${isFocus ? ' focus' : ''}`}
                      onMouseDown={(e) => cellMouseDown(e, r, c)}
                      onMouseEnter={() => cellMouseEnter(r, c)}
                      onDoubleClick={() => setEditing({ r, c, value: item ? `${item.lat}, ${item.lng}` : '' })}
                    >
                      {isEditing ? (
                        <input
                          autoFocus
                          className="coord-edit"
                          value={editing.value}
                          onChange={(e) => setEditing({ r, c, value: e.target.value })}
                          onBlur={() => { saveCell(r, c, editing.value); setEditing(null); }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') { saveCell(r, c, editing.value); setEditing(null); }
                            if (e.key === 'Escape') setEditing(null);
                          }}
                        />
                      ) : item ? (
                        <div className="coord-cell" style={{ opacity: item.usedAt ? 0.4 : 1 }}>
                          <span
                            className="coord-value"
                            style={{
                              color: copied === item.id ? '#4ade80' : undefined,
                              textDecoration: item.usedAt ? 'line-through' : 'none',
                            }}
                          >
                            {copied === item.id ? 'скопировано' : `${item.lat}, ${item.lng}`}
                          </span>
                          <span className="coord-actions">
                            <button
                              className="row-open-btn"
                              title="Скопировать"
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => {
                                navigator.clipboard.writeText(`${item.lat}, ${item.lng}`).catch(() => {});
                                setCopied(item.id);
                                setTimeout(() => setCopied((v) => (v === item.id ? null : v)), 1000);
                              }}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" /><path d="M5 15V5a2 2 0 0 1 2-2h10" /></svg>
                            </button>
                            <button
                              className="row-open-btn"
                              title={
                                sel.has(keyOf(r, c)) && sel.size > 1
                                  ? `Отметить использованными: ${sel.size} шт.`
                                  : item.usedAt ? 'Вернуть в неиспользованные' : 'Отметить использованной'
                              }
                              style={{ color: item.usedAt ? 'var(--text-subtle)' : '#4ade80' }}
                              onMouseDown={(e) => e.stopPropagation()}
                              onClick={() => toggleUsed(item)}
                            >
                              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round"><polyline points="20 6 9 17 4 12" /></svg>
                            </button>
                          </span>
                        </div>
                      ) : null}
                    </td>
                  );
                })}
                <td />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {sel.size > 1 && (
        <div className="coord-selinfo">
          <span>{sel.size} яч. · Ctrl+C/X/V · Del · Cmd+Z</span>
          <button
            className="btn-secondary"
            style={{ height: 26, padding: '0 12px', fontSize: 12 }}
            disabled={busy}
            onClick={markSelectionUsed}
          >
            ✓ Отметить использованными
          </button>
        </div>
      )}
    </div>
  );
}
